import React, {useState} from 'react';
import apiClient2 from "../../../../api/apiClient2";
import apiPaths from "../../../../api/endpoints";
import {CardColor, ChatQuestion, ChatQuestionOption, CARD_BORDER_COLORS, CARD_BG_IMAGES} from '../config';
import WebAudioPlayer from '../../WebStoryScreen/Components/WebAudioPlayer';
import WebShadowButton from '../../WebStoryScreen/Components/WebShadowButton';
import closeIcon from '../../../appAssets/closeIcon.png';
import {stripSignedUrlQuery} from '../../../Utilities/utilFunctions';
import { trackEvent } from '../../../../mixpanel';
import { TrackingEvents } from '../../../Utilities/analytics';

interface QuestionModalProps {
  question: ChatQuestion | null;
  cardColor: CardColor;
  onSubmit: (option: ChatQuestionOption) => void;
  onClose: () => void;
}

const QuestionModal: React.FC<QuestionModalProps> = ({
  question,
  cardColor,
  onSubmit,
  onClose,
}) => {
  const isLarge = window.innerWidth >= 768;

  const [selectedOption, setSelectedOption] = useState<ChatQuestionOption | null>(null);
  const [activeAudioUrl, setActiveAudioUrl] = useState<string | null>(
    question?.audioUrl ?? null,
  );
  const [submitting, setSubmitting] = useState(false);

  if (!question) return null;

  const handleSubmit = async () => {
    if (!selectedOption) return;
    setSubmitting(true);
    trackEvent(TrackingEvents.child_answers_to_troo,{
      type: question.type,
      complexity: question.complexity,
      selCompetency: question.selCompetency,
      answer: selectedOption.option,
    });
    try {
      await apiClient2.post(apiPaths.CHAT_RESPONSE, {
        documentId: question.documentId,
        question: question.question,
        type: question.type,
        complexity: question.complexity,
        selCompetency: question.selCompetency,
        storyName: question.storyName,
        storyImageUrl: question.storyImageUrl,
        selectedOption: {
          option: selectedOption.option,
          optionValue: selectedOption.optionValue,
        },
        compassAttribute: question.compassAttribute,
        critical: question.critical,
      });
    } catch {
      // Submit best-effort; continue regardless
    } finally {
      setSubmitting(false);
      onSubmit(selectedOption);
    }
  };

  const handleOptionSelect = (option: ChatQuestionOption) => {
    setSelectedOption(option);
    if (option.optionAudioUrl) {
      setActiveAudioUrl(option.optionAudioUrl);
    }
  };

  const borderColor = CARD_BORDER_COLORS[cardColor];
  const bgImage = CARD_BG_IMAGES[cardColor];
  const isFewOptions = question.options.length <= 3;
  const modalWidth = Math.min(window.innerWidth * 0.92, isLarge ? 720 : 580);
  const modalHeight = Math.min(window.innerHeight * 0.88, isLarge ? 600 : 520);

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const containerStyle: React.CSSProperties = {
    backgroundImage: `url(${bgImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderRadius: 20,
    padding: 16,
    border: `3px solid ${borderColor}`,
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
    width: modalWidth,
    height: modalHeight,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  };

  const questionTextStyle: React.CSSProperties = {
    flex: 1,
    textAlign:'left',
    fontSize: 24,
    color: '#4E4646',
    fontFamily: 'DynaPuff',
    fontWeight: 500,
    lineHeight: isLarge ? '25px' : '20px',
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 18,
    color: '#888',
    fontWeight: 600,
    padding: 4,
  };

  const optionsScrollStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    alignItems: 'center',
  };

  const optionsContentStyle: React.CSSProperties = {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: question.options.length === 4 ? '1fr 1fr' : 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    padding: '8px 0',
  };

  const submitRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 12,
  };

  return (
    <div style={overlayStyle}>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <p style={questionTextStyle}>{question.question}</p>
          <button style={closeButtonStyle} onClick={onClose}>
            <img src={closeIcon} alt="close" style={{width: isLarge ? 28 : 22, height: isLarge ? 28 : 22}} />
          </button>
        </div>

        <div style={optionsScrollStyle}>
          <div style={optionsContentStyle}>
            {question.options.map(option => {
              const isSelected = selectedOption?.optionValue === option.optionValue;
              return (
                <WebShadowButton
                  key={option.optionValue}
                  title={option.option}
                  variant={isSelected ? 'text' : 'outline'}
                  onPress={() => handleOptionSelect(option)}
                  labelStyle={{ fontFamily: 'Mochiy Pop One', fontWeight: 400, whiteSpace: 'normal', textAlign: 'center' }}
                  style={{ width: '100%', minWidth: 0 }}
                />
              );
            })}
          </div>
        </div>

        <div style={submitRowStyle}>
          <WebShadowButton
            title="Done"
            variant="text"
            disabled={!selectedOption || submitting}
            onPress={handleSubmit}
          />
        </div>

        {activeAudioUrl ? (
          <WebAudioPlayer key={stripSignedUrlQuery(activeAudioUrl)} uri={activeAudioUrl} />
        ) : null}
      </div>
    </div>
  );
};

export default QuestionModal;
